import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth, useLanguage } from '../App';
import { adminAPI } from '../lib/api';
import { t } from '../lib/i18n';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Inbox, Search, RefreshCw } from 'lucide-react';
import { SupportMessageCard } from '../components/admin/SupportMessageCard';

const STATUSES = ['new', 'handled', 'all'];

export default function AdminSupportPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [status, setStatus] = useState('new');
  const [search, setSearch] = useState('');
  const [messages, setMessages] = useState([]);
  const [counts, setCounts] = useState({ new: 0, handled: 0, all: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await adminAPI.supportList(status, search.trim());
      setMessages(res.data.messages || []);
      setCounts(res.data.counts || { new: 0, handled: 0, all: 0 });
    } catch (err) {
      setError(true);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [status, search]);

  useEffect(() => {
    const handle = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(handle);
  }, [load, search]);

  if (user && !user.is_admin) return <Navigate to="/dashboard" replace />;

  const handleUpdate = async (id, newStatus, note) => {
    const previous = messages.find(m => m.id === id);
    try {
      const res = await adminAPI.supportUpdate(id, newStatus, note ?? null);
      const updated = res.data.message;
      setMessages(prev => {
        const next = prev.map(m => (m.id === id ? { ...m, ...updated } : m));
        return status === 'all' ? next : next.filter(m => m.status === status);
      });
      if (previous && previous.status !== updated.status) {
        setCounts(prev => ({
          new: Math.max(0, prev.new + (updated.status === 'new' ? 1 : -1)),
          handled: Math.max(0, prev.handled + (updated.status === 'handled' ? 1 : -1)),
          all: prev.all,
        }));
      }
      toast.success(language === 'fr' ? 'Message mis \u00e0 jour' : 'Message updated');
    } catch (err) {
      toast.error(language === 'fr' ? 'Mise \u00e0 jour impossible' : 'Update failed');
      throw err;
    }
  };

  const handleReply = async (id, body, markHandled) => {
    const previous = messages.find(m => m.id === id);
    try {
      const res = await adminAPI.supportReply(id, body, markHandled);
      const updated = res.data.message;
      setMessages(prev => {
        const next = prev.map(m => (m.id === id ? { ...m, ...updated } : m));
        return status === 'all' ? next : next.filter(m => m.status === status);
      });
      if (previous && previous.status !== updated.status) {
        setCounts(prev => ({
          new: Math.max(0, prev.new + (updated.status === 'new' ? 1 : -1)),
          handled: Math.max(0, prev.handled + (updated.status === 'handled' ? 1 : -1)),
          all: prev.all,
        }));
      }
      toast.success(`${t('replySent', language)} (${res.data.to})`);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail === 'no_recipient_email') toast.error(t('replyNoRecipient', language));
      else if (detail === 'email_not_configured') toast.error(t('reminderNotConfigured', language));
      else toast.error(t('replyError', language));
      throw err;
    }
  };

  const labelFor = (s) => ({ new: t('statusNew', language), handled: t('statusHandled', language), all: t('statusAll', language) }[s]);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-6 max-w-4xl" data-testid="admin-support-page">
      <div className="fade-in flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-white flex items-center gap-2"><Inbox className="w-7 h-7 text-[var(--lux-gold)]" /> {t('adminInbox', language)}</h1>
          <p className="text-white/50 text-sm mt-1">{t('adminInboxSubtitle', language)}</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="bg-[var(--lux-panel)] border-white/10 text-white hover:bg-[var(--lux-panel-2)]" data-testid="admin-support-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> {language === 'fr' ? 'Actualiser' : 'Refresh'}
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList className="bg-[var(--lux-panel)] border border-white/[0.08]">
            {STATUSES.map(s => (
              <TabsTrigger key={s} value={s} data-testid={`admin-support-tab-${s}`} className="data-[state=active]:bg-[var(--lux-gold)] data-[state=active]:text-[var(--lux-ink)] text-white/60">
                {labelFor(s)} <span className="ml-1.5 text-[10px] opacity-70">({counts[s] ?? 0})</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <Input
            data-testid="admin-support-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('searchMessages', language)}
            className="pl-10 bg-black/20 border-white/10 text-white placeholder:text-white/30"
          />
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[0, 1, 2].map(i => <Skeleton key={i} className="h-32 w-full bg-white/5 rounded-xl" />)}</div>
      ) : error ? (
        <Card className="bg-lux-panel/90 border-white/[0.08]"><CardContent className="py-12 text-center space-y-3">
          <p className="text-white/40 text-sm">{language === 'fr' ? 'Impossible de charger les messages' : 'Could not load messages'}</p>
          <Button onClick={load} variant="outline" className="bg-[var(--lux-panel)] border-white/10 text-white">{language === 'fr' ? 'R\u00e9essayer' : 'Retry'}</Button>
        </CardContent></Card>
      ) : messages.length === 0 ? (
        <Card className="bg-lux-panel/90 border-white/[0.08]" data-testid="admin-support-empty"><CardContent className="py-16 text-center">
          <Inbox className="w-8 h-8 text-white/10 mx-auto mb-3" />
          <p className="text-white/30 text-sm">{t('noMessages', language)}</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3" data-testid="admin-support-list">
          {messages.map(m => <SupportMessageCard key={m.id} message={m} language={language} onUpdate={handleUpdate} onReply={handleReply} />)}
        </div>
      )}
    </div>
  );
}
